/**
 * Быстрое добавление элемента.
 *
 * Первый шаг — выбор типа из сетки пятнадцати, второй — компактная форма.
 * Обязательно только название; специальные поля показываются лишь те, что
 * реестр отметил как важнейшие для типа (`quickAddProperties`).
 *
 * Главный критерий: зафиксировать результат работы должно быть быстрее, чем
 * потерять его в личной заметке (документ «Итоговое описание», §23).
 */

import { ArrowLeftIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  getItemTypeDefinition,
  getQuickAddProperties,
  getItemTypesByGroup,
  ITEM_GROUPS,
  ITEM_GROUP_LABELS_RU,
  type ItemType,
  type UserDto,
} from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useQuickAddItem } from '@/api/items';
import { useProjectMembers } from '@/api/members';
import { useWorkstreams } from '@/api/workstreams';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getItemIcon } from './item-icon';
import { PropertyField } from './PropertyField';

const NONE = '__none__';

interface QuickAddForm {
  title: string;
  bodyMd: string;
  workstreamId: string;
  ownerId: string;
  properties: Record<string, unknown>;
  detail: Record<string, unknown>;
}

export function QuickAddDialog({
  open,
  onOpenChange,
  projectId,
  defaultType,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  defaultType?: ItemType;
  onCreated?: (itemId: string) => void;
}) {
  const [type, setType] = useState<ItemType | null>(defaultType ?? null);
  const quickAdd = useQuickAddItem(projectId);
  const { data: workstreams = [] } = useWorkstreams(projectId);
  const { data: members = [] } = useProjectMembers(projectId);

  const memberUsers: UserDto[] = members.map((member) => member.user);

  const form = useForm<QuickAddForm>({
    defaultValues: {
      title: '',
      bodyMd: '',
      workstreamId: NONE,
      ownerId: NONE,
      properties: {},
      detail: {},
    },
  });

  useEffect(() => {
    if (open) {
      setType(defaultType ?? null);
      form.reset({
        title: '',
        bodyMd: '',
        workstreamId: NONE,
        ownerId: NONE,
        properties: {},
        // Валюта по умолчанию нужна сразу: денежное поле форматирует по ней.
        detail: { currency: 'EUR' },
      });
    }
  }, [open, defaultType, form]);

  const definition = type ? getItemTypeDefinition(type) : null;
  const quickProperties = type ? getQuickAddProperties(type) : [];

  const onSubmit = form.handleSubmit(async (values) => {
    if (!type) return;

    // Пустые значения не отправляются: пустая строка не должна попадать
    // в properties и проходить проверку формата.
    const properties = Object.fromEntries(
      Object.entries(values.properties).filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
    const detail = Object.fromEntries(
      Object.entries(values.detail).filter(
        ([, value]) => value !== null && value !== undefined && value !== '',
      ),
    );

    try {
      const item = await quickAdd.mutateAsync({
        type,
        title: values.title,
        bodyMd: values.bodyMd || null,
        workstreamId: values.workstreamId === NONE ? null : values.workstreamId,
        ownerId: values.ownerId === NONE ? null : values.ownerId,
        properties,
        detail: Object.keys(detail).length > 1 ? detail : null,
      });
      toast.success(`${getItemTypeDefinition(type).labelRu} добавлен`);
      onOpenChange(false);
      onCreated?.(item.id);
    } catch (error) {
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
      }
    }
  });

  const formError =
    quickAdd.error instanceof ApiError && !quickAdd.error.fields ? quickAdd.error : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {!type ? (
          <>
            <DialogHeader>
              <DialogTitle>Что добавить?</DialogTitle>
              <DialogDescription>
                Тип определяет, какие поля будут доступны. Изменить его после создания нельзя, но
                элемент всегда можно связать с другим.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {ITEM_GROUPS.map((group) => (
                <section key={group}>
                  <h3 className="mb-2 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
                    {ITEM_GROUP_LABELS_RU[group]}
                  </h3>
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    {getItemTypesByGroup(group).map((item) => {
                      const Icon = getItemIcon(item.type);
                      return (
                        <button
                          key={item.type}
                          type="button"
                          onClick={() => setType(item.type)}
                          title={item.descriptionRu}
                          className={cn(
                            'flex items-center gap-2 rounded-md border px-2.5 py-2 text-left text-sm',
                            'transition-colors hover:border-border-strong hover:bg-accent',
                          )}
                        >
                          <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="truncate">{item.labelRu}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {defaultType ? null : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="-ml-2 size-7"
                    onClick={() => setType(null)}
                    aria-label="Выбрать другой тип"
                  >
                    <ArrowLeftIcon className="size-4" />
                  </Button>
                )}
                Новый элемент: {definition?.labelRu.toLowerCase()}
              </DialogTitle>
              <DialogDescription>{definition?.descriptionRu}</DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              {formError ? (
                <Alert variant="destructive">
                  <AlertDescription>{formError.message}</AlertDescription>
                </Alert>
              ) : null}

              {definition?.warningRu ? (
                <Alert>
                  <AlertDescription className="text-xs leading-relaxed">
                    {definition.warningRu}
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="quick-title" className="gap-1">
                  Название
                  <span className="text-destructive" aria-hidden>
                    *
                  </span>
                </Label>
                <Input
                  id="quick-title"
                  autoFocus
                  {...form.register('title', { required: 'Укажите название' })}
                  aria-invalid={Boolean(form.formState.errors.title)}
                />
                {form.formState.errors.title ? (
                  <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
                ) : null}
              </div>

              {quickProperties.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {quickProperties.map((property) => (
                    <PropertyField
                      key={property.key}
                      control={form.control}
                      name={
                        (property.storage === 'detail'
                          ? `detail.${property.key}`
                          : `properties.${property.key}`) as never
                      }
                      currencyName={'detail.currency' as never}
                      property={property}
                      members={memberUsers}
                    />
                  ))}
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="quick-body">Описание</Label>
                <Textarea id="quick-body" rows={3} {...form.register('bodyMd')} />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="quick-workstream">Направление</Label>
                  <Select
                    value={form.watch('workstreamId')}
                    onValueChange={(value) => form.setValue('workstreamId', value)}
                  >
                    <SelectTrigger id="quick-workstream" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Без направления</SelectItem>
                      {workstreams.map((workstream) => (
                        <SelectItem key={workstream.id} value={workstream.id}>
                          {workstream.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="quick-owner">Ответственный</Label>
                  <Select
                    value={form.watch('ownerId')}
                    onValueChange={(value) => form.setValue('ownerId', value)}
                  >
                    <SelectTrigger id="quick-owner" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Не назначен</SelectItem>
                      {memberUsers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs leading-relaxed text-muted-foreground">
                Остальные поля можно заполнить позже — незаполненное не мешает сохранить элемент.
              </p>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={quickAdd.isPending}>
                  {quickAdd.isPending ? <Loader2Icon className="animate-spin" /> : null}
                  Добавить
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
